<template>
  <dialog
    ref="dialog"
    class="aw-confirm-dialog"
    :class="['aw-confirm-dialog--' + type, { 'aw-confirm-dialog--structured': request && request.content.length }]"
    :role="['warning', 'danger'].includes(type) ? 'alertdialog' : 'dialog'"
    aria-modal="true"
    :aria-labelledby="titleId"
    :aria-describedby="messageId"
    @cancel.prevent="answer(false)"
    @keydown="onKeydown"
    @pointerdown="backdropPressed = isBackdrop($event)"
    @click="onBackdropClick"
  >
    <template v-if="request">
      <header class="aw-confirm-dialog__header">
        <span class="aw-confirm-dialog__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" focusable="false">
            <template v-if="type === 'warning'">
              <path d="M10.3 3.8 2.5 17.3A2 2 0 0 0 4.2 20h15.6a2 2 0 0 0 1.7-2.7L13.7 3.8a2 2 0 0 0-3.4 0Z" />
              <path d="M12 9v4m0 3h.01" />
            </template>
            <template v-else-if="type === 'danger'">
              <path d="m8.3 3-5.3 5.3v7.4L8.3 21h7.4l5.3-5.3V8.3L15.7 3Z" />
              <path d="M12 7v6m0 4h.01" />
            </template>
            <template v-else-if="type === 'success'">
              <circle cx="12" cy="12" r="9" /><path d="m8 12 2.5 2.5L16 9" />
            </template>
            <template v-else>
              <circle cx="12" cy="12" r="9" /><path d="M12 11v6m0-10h.01" />
            </template>
          </svg>
        </span>
        <h2 :id="titleId" class="aw-confirm-dialog__title">{{ request.title || $t('confirm_dialog.title') }}</h2>
        <button type="button" class="aw-confirm-dialog__close" :aria-label="$t('confirm_dialog.close')" @click="answer(false)">
          <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="m6 6 12 12M18 6 6 18" /></svg>
        </button>
      </header>
      <div class="aw-confirm-dialog__body" tabindex="0">
        <div v-if="request.content.length" :id="messageId" class="aw-confirm-dialog__message aw-confirm-dialog__message--structured">
          <section v-for="(block, index) in request.content" :key="index" class="aw-confirm-dialog__section" :class="'aw-confirm-dialog__section--' + block.type">
            <h3 v-if="block.title" class="aw-confirm-dialog__section-title">{{ block.title }}</h3>
            <p v-if="block.text" class="aw-confirm-dialog__text">{{ block.text }}</p>
            <div v-for="(group, groupIndex) in block.groups" :key="groupIndex" class="aw-confirm-dialog__group">
              <h4 class="aw-confirm-dialog__group-title">
                <span>{{ group.title }}</span><span v-if="group.count !== null" class="aw-confirm-dialog__count">{{ group.count }}</span>
              </h4>
              <ul v-if="group.items.length" class="aw-confirm-dialog__items">
                <li v-for="(item, itemIndex) in group.items" :key="itemIndex">{{ item }}</li>
              </ul>
              <p v-if="group.more" class="aw-confirm-dialog__more">{{ group.more }}</p>
            </div>
          </section>
        </div>
        <p v-else :id="messageId" class="aw-confirm-dialog__message">{{ request.message }}</p>
      </div>
      <footer class="aw-confirm-dialog__footer">
        <button v-if="request.button.no !== false" ref="cancel" type="button" class="aw-confirm-dialog__button aw-confirm-dialog__button--cancel" @click="answer(false)">
          {{ request.button.no || $t('confirm_dialog.cancel') }}
        </button>
        <button ref="confirm" type="button" class="aw-confirm-dialog__button aw-confirm-dialog__button--confirm" @click="answer(true)">
          {{ request.button.yes || $t('confirm_dialog.confirm') }}
        </button>
      </footer>
    </template>
  </dialog>
</template>

<script>
import { confirmService } from '@/services/confirm';

export default {
  name: 'AppConfirmDialog',
  props: { service: { type: Object, default: () => confirmService } },
  data: () => ({ backdropPressed: false }),
  computed: {
    request() { return this.service.state.current; },
    type() { return this.request?.type || 'info'; },
    titleId() { return `aw-confirm-title-${this._uid}`; },
    messageId() { return `aw-confirm-message-${this._uid}`; },
  },
  watch: {
    request: {
      immediate: true,
      handler(request) {
        if (!request) { this.closeDialog(); return; }
        this.$nextTick(() => {
          if (this._isDestroyed || this.request?.id !== request.id) return;
          this.opener = document.activeElement;
          this.backdropPressed = false;
          this.$refs.dialog.showModal();
          document.documentElement.classList.add('aw-confirm-open');
          (this.$refs.cancel || this.$refs.confirm).focus({ preventScroll: true });
        });
      },
    },
    '$route.fullPath'() { this.service.confirm.closeAll(); },
  },
  beforeDestroy() {
    this.service.confirm.closeAll();
    this.closeDialog();
  },
  methods: {
    answer(confirmed) {
      if (this.request) this.service.answer(this.request.id, confirmed);
    },
    closeDialog() {
      if (!this.$refs.dialog?.open) return;
      this.$refs.dialog.close();
      document.documentElement.classList.remove('aw-confirm-open');
      if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      this.opener = null;
    },
    isBackdrop(event) {
      if (event.target !== this.$refs.dialog) return false;
      const bounds = this.$refs.dialog.getBoundingClientRect();
      return event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom;
    },
    onBackdropClick(event) {
      if (this.backdropPressed && this.isBackdrop(event) && this.request?.dismissOnBackdrop) this.answer(false);
      this.backdropPressed = false;
    },
    onKeydown(event) {
      // Keep Escape and tab handling inside the topmost modal.
      event.stopPropagation();
      if (event.key === 'Escape') { event.preventDefault(); this.answer(false); return; }
      if (event.key !== 'Tab') return;
      const items = [...this.$refs.dialog.querySelectorAll('button:not(:disabled), [tabindex="0"]')];
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    },
  },
};
</script>

<style lang="scss">
html.aw-confirm-open { overflow: hidden; overscroll-behavior: none; }

.aw-confirm-dialog {
  --confirm-accent: #086f86;
  --confirm-accent-hover: #075c70;
  --confirm-tint: #eaf6f8;
  position: fixed;
  inset: 0;
  inset: env(safe-area-inset-top, 0px) env(safe-area-inset-right, 0px) env(safe-area-inset-bottom, 0px) env(safe-area-inset-left, 0px);
  width: calc(100% - 2rem);
  width: calc(100% - 2rem - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px));
  max-width: 28rem;
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px));
  margin: auto;
  padding: 0;
  overflow: hidden;
  border: 1px solid rgba(17, 24, 39, .08);
  border-radius: 1.25rem;
  background: #fff;
  color: #18212f;
  box-shadow: 0 24px 80px rgba(15, 23, 42, .2), 0 2px 8px rgba(15, 23, 42, .06);
  text-align: left;
  &[open] { display: flex; flex-direction: column; }
  &::backdrop { background: rgba(15, 23, 42, .42); }
  &--warning { --confirm-accent: #946000; --confirm-accent-hover: #7b4f00; --confirm-tint: #fff5df; }
  &--danger { --confirm-accent: #c5333c; --confirm-accent-hover: #ab2730; --confirm-tint: #fdf0f1; }
  &--success { --confirm-accent: #18734b; --confirm-accent-hover: #115b3b; --confirm-tint: #eaf7f0; }
  &--structured { max-width: 36rem; }

  &__header { display: flex; flex: 0 0 auto; align-items: center; gap: .875rem; padding: 1.5rem 1.25rem 1rem 1.5rem; }
  &__icon { display: grid; flex: 0 0 2.75rem; place-items: center; width: 2.75rem; height: 2.75rem; border-radius: .875rem; background: var(--confirm-tint); color: var(--confirm-accent); }
  &__icon svg { width: 1.5rem; height: 1.5rem; }
  &__title { flex: 1; min-width: 0; margin: 0; color: inherit; font-size: 1.125rem; font-weight: 600; line-height: 1.4; letter-spacing: -.02em; overflow-wrap: anywhere; }
  &__close { display: grid; flex: 0 0 2.75rem; place-items: center; width: 2.75rem; height: 2.75rem; margin: -.375rem -.375rem -.375rem 0; padding: .75rem; border: 0; border-radius: .75rem; background: transparent; color: #6b7280; }
  &__close svg { width: 1.125rem; height: 1.125rem; }
  &__body { min-height: 0; padding: 0 1.5rem 1.5rem; overflow-y: auto; overscroll-behavior: contain; -webkit-overflow-scrolling: touch; }
  &__message { margin: 0; color: #536171; font-size: .9375rem; line-height: 1.65; white-space: pre-line; overflow-wrap: anywhere; }
  &__message--structured { display: grid; gap: 1.25rem; white-space: normal; }
  &__section { min-width: 0; }
  &__section-title { margin: 0 0 .625rem; color: #536171; font-size: .8125rem; font-weight: 600; line-height: 1.5; }
  &__text { margin: 0; white-space: pre-line; }
  &__section--record { padding: .875rem 1rem; border: 1px solid #dcecef; border-radius: .75rem; background: var(--confirm-tint); }
  &__section--record &__section-title { margin-bottom: .25rem; }
  &__section--record &__text { color: #18212f; font-weight: 600; }
  &__group { padding: .75rem .875rem; border: 1px solid #e6ebf0; border-radius: .75rem; }
  &__group + &__group { margin-top: .625rem; }
  &__group-title { display: flex; align-items: baseline; justify-content: space-between; gap: .75rem; margin: 0; color: #374151; font-size: .875rem; font-weight: 600; line-height: 1.5; }
  &__group-title > span:first-child { min-width: 0; }
  &__count { flex: 0 0 auto; min-width: 1.5rem; padding: .0625rem .4375rem; border-radius: .375rem; background: var(--confirm-tint); color: var(--confirm-accent); font-size: .75rem; text-align: center; font-variant-numeric: tabular-nums; }
  &__items { margin: .625rem 0 0; padding-left: 1.125rem; font-size: .875rem; }
  &__items li + li { margin-top: .5rem; padding-top: .5rem; border-top: 1px solid #f0f3f6; }
  &__items li::marker { color: #94a3b8; }
  &__more { margin: .5rem 0 0; color: #687787; font-size: .8125rem; }
  &__section--note { color: #687787; font-size: .8125rem; }
  &__section--note + &__section--note { margin-top: -.625rem; }
  &__section--question { color: #18212f; font-weight: 600; }
  &__footer { display: flex; flex: 0 0 auto; justify-content: flex-end; gap: .625rem; padding: 1rem 1.5rem; border-top: 1px solid #edf0f3; background: #fbfcfd; }
  &__button { min-width: 6rem; min-height: 2.75rem; padding: .625rem 1.125rem; border: 1px solid transparent; border-radius: .75rem; font: inherit; font-size: .9375rem; font-weight: 600; line-height: 1.4; overflow-wrap: anywhere; }
  &__button--cancel { border-color: #dfe4ea; background: #fff; color: #374151; }
  &__button--confirm { background: var(--confirm-accent); color: #fff; }
  &__button, &__close { cursor: pointer; touch-action: manipulation; user-select: none; -webkit-tap-highlight-color: transparent; transition: background-color 120ms ease, transform 120ms ease-out; }
  &__button:active, &__close:active { transform: scale(.98); }
  &__button:focus-visible, &__close:focus-visible, &__body:focus-visible { outline: 2px solid var(--confirm-accent); outline-offset: 3px; }
  @media (hover: hover) and (pointer: fine) {
    &__button--cancel:hover, &__close:hover { background: #f0f3f6; }
    &__button--confirm:hover { background: var(--confirm-accent-hover); }
  }
  @media (max-width: 480px) {
    width: calc(100% - 1.5rem);
    width: calc(100% - 1.5rem - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px));
    &__header { padding: 1.25rem 1rem .875rem 1.25rem; }
    &__body { padding: 0 1.25rem 1.25rem; }
    &__footer { padding: 1rem 1.25rem; }
    &__button { flex: 1; min-width: 0; }
  }
  @media (prefers-reduced-motion: reduce) {
    &__button, &__close { transition: none; }
    &__button:active, &__close:active { transform: none; }
  }
}
</style>
